import React, { useState } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { Drawer } from '@/shared/components/ui/Drawer';
import { ProviderGrid } from './ProviderGrid';
import {
  GatewayProviderForm,
  WhatsAppBusinessForm,
  InstagramForm,
  EmailSMTPForm,
  EmailResendForm,
  FacebookMessengerForm,
  TelegramBotForm,
  WidgetForm,
} from './forms';
import { useCreateChannel, useReconnectChannel } from '../hooks';
import { Icon } from '@/shared/icons'
/**
 * Multi-step wizard for creating channels (Drawer)
 *
 * @example
 * <CreateChannelModal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onSuccess={(channel) => handleSuccess(channel)}
 * />
 */
export interface CreateChannelModalProps {
  /** Drawer open state */
  isOpen: boolean;
  /** Callback when drawer should close */
  onClose: () => void;
  /** Callback when channel is created successfully */
  onSuccess?: (channel: any) => void;
}

export type WizardStep = 'provider-selection' | 'authentication' | 'configuration';

export const CreateChannelModal: React.FC<CreateChannelModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [currentStep, setCurrentStep] = useState<WizardStep>('provider-selection');
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [createdChannel, setCreatedChannel] = useState<any>(null);
  const [qrCodeData, setQrCodeData] = useState<{ qrCode?: string; qrCodeExpires?: number; instanceKey?: string }>({});
  const [isChannelConnected, setIsChannelConnected] = useState(false);

  const createChannel = useCreateChannel();
  const reconnectChannel = useReconnectChannel();

  const handleProviderSelect = (providerId: string) => {
    setSelectedProvider(providerId);
    setCurrentStep('authentication');
  };

  const handleAuthComplete = (authData?: any) => {
    if (!authData) {
      handleClose();
      return;
    }
    handleCreateChannel(authData);
  };

  const handleConnectionComplete = () => {
    handleClose();
  };

  const handleConnectionEstablished = () => {
    setIsChannelConnected(true);
  };

  const handleCreateChannel = async (data: any) => {
    try {
      if (!data.name || data.name.trim().length < 2) {
        console.error('Nome do canal deve ter pelo menos 2 caracteres');
        return;
      }

      if (!data.identifier || data.identifier.trim().length === 0) {
        console.error('Identificador do canal é obrigatório');
        return;
      }

      const channelRequest = {
        name: data.name.trim(),
        identifier: data.identifier.trim(),
        providerId: data.providerId,
        config: data.config || {},
        credentials: data.credentials || {},
        integrationName: `${data.name.trim()} Integration`,
        assignmentConfig: {
          enabled: true,
          type: 'sequential' as const,
        },
      };

      const channel = await createChannel.mutateAsync(channelRequest);

      if (data.providerId === 'gateway-whatsapp') {
        try {
          const reconnectResult = await reconnectChannel.mutateAsync({ id: channel.id });

          if (reconnectResult.qrCode) {
            setCreatedChannel(channel);
            setQrCodeData({
              qrCode: reconnectResult.qrCode,
              qrCodeExpires: reconnectResult.qrCodeExpires,
              instanceKey: reconnectResult.instanceKey,
            });
            return;
          }
        } catch (reconnectError) {
          console.error('Erro ao obter QR Code:', reconnectError);
        }
      }

      if (onSuccess) {
        onSuccess(channel);
      }

      handleClose();
    } catch (error) {
      console.error('Error creating channel:', error);
    }
  };

  const handleBack = () => {
    if (currentStep === 'authentication') {
      setCurrentStep('provider-selection');
      setSelectedProvider(null);
    } else if (currentStep === 'configuration') {
      setCurrentStep('authentication');
    }
  };

  const handleClose = () => {
    setCurrentStep('provider-selection');
    setSelectedProvider(null);
    onClose();
  };

  const stepTitles = {
    'provider-selection': 'Selecione a integração',
    'authentication': 'Configuração de Autenticação',
    'configuration': 'Configuração do Canal',
  };

  const stepDescriptions = {
    'provider-selection': 'Escolha a integração que deseja conectar',
    'authentication': `Configure a conexão com ${getProviderName(selectedProvider)}`,
    'configuration': 'Configure as opções de atribuição e operadores',
  };

  const drawerHeader = (
    <div>
      {/* Title + Close button */}
      <div
        className="flex items-center justify-between px-6 py-4 border-b"
        style={{ borderColor: '#e5e7eb' }}
      >
        <div>
          <h2
            className="text-lg font-semibold"
            style={{ color: '#1e293b' }}
          >
            {stepTitles[currentStep]}
          </h2>
          <p
            className="text-sm mt-0.5"
            style={{ color: '#64748b' }}
          >
            {stepDescriptions[currentStep]}
          </p>
        </div>
        <button
          onClick={handleClose}
          className="p-1.5 rounded-lg transition-colors"
          style={{ color: '#64748b' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f1f5f9';
            e.currentTarget.style.color = '#1e293b';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = '#64748b';
          }}
        >
          <Icon name="x" className="w-5 h-5" />
        </button>
      </div>

      {/* Progress Steps */}
      <div
        className="flex items-center justify-center gap-2 px-6 py-3 border-b"
        style={{ borderColor: '#e5e7eb' }}
      >
        <StepIndicator
          step={1}
          label="Integração"
          active={currentStep === 'provider-selection'}
          completed={currentStep !== 'provider-selection'}
        />
        <div className="w-12 h-0.5" style={{ backgroundColor: '#e5e7eb' }} />
        <StepIndicator
          step={2}
          label="Autenticação"
          active={currentStep === 'authentication'}
          completed={isChannelConnected}
        />
      </div>
    </div>
  );

  const drawerFooter = !isChannelConnected ? (
    <>
      <Button onClick={handleClose} variant="ghost" size="md">
        Cancelar
      </Button>
      {currentStep !== 'provider-selection' && (
        <Button onClick={handleBack} variant="ghost" size="md">
          Voltar
        </Button>
      )}
    </>
  ) : undefined;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      customHeader={drawerHeader}
      footer={drawerFooter}
    >
      <div className="px-6 py-5">
      {currentStep === 'provider-selection' && (
        <ProviderGrid
          selectedProvider={selectedProvider}
          onProviderSelect={handleProviderSelect}
        />
      )}

      {currentStep === 'authentication' && selectedProvider === 'gateway-whatsapp' && (
        <GatewayProviderForm
          onComplete={createdChannel ? handleConnectionComplete : handleAuthComplete}
          onConnectionEstablished={handleConnectionEstablished}
          initialQrCode={qrCodeData.qrCode}
          initialQrCodeExpires={qrCodeData.qrCodeExpires}
          channelId={createdChannel?.id}
          initialEngine={createdChannel?.config?.engine as 'node' | 'go' | undefined}
          initialInstanceKey={qrCodeData.instanceKey}
        />
      )}

      {currentStep === 'authentication' && selectedProvider === 'whatsapp-business' && (
        <WhatsAppBusinessForm onComplete={handleAuthComplete} />
      )}

      {currentStep === 'authentication' && selectedProvider === 'instagram-messaging' && (
        <InstagramForm onComplete={handleAuthComplete} />
      )}

      {currentStep === 'authentication' && selectedProvider === 'facebook-messenger' && (
        <FacebookMessengerForm onComplete={handleAuthComplete} />
      )}

      {currentStep === 'authentication' && selectedProvider === 'telegram-bot' && (
        <TelegramBotForm onComplete={handleAuthComplete} />
      )}

      {currentStep === 'authentication' && selectedProvider === 'email-smtp' && (
        <EmailSMTPForm onComplete={handleAuthComplete} />
      )}

      {currentStep === 'authentication' && selectedProvider === 'email-resend' && (
        <EmailResendForm onComplete={handleAuthComplete} />
      )}

      {currentStep === 'authentication' && selectedProvider === 'website-widget' && (
        <WidgetForm onComplete={handleAuthComplete} />
      )}

      {currentStep === 'configuration' && (
        <div>
          <p style={{ color: '#1e293b' }}>
            Configuração de assignment será implementada...
          </p>
        </div>
      )}
      </div>
    </Drawer>
  );
};

CreateChannelModal.displayName = 'CreateChannelModal';

// Helper function to get provider display name
function getProviderName(providerId: string | null): string {
  const names: Record<string, string> = {
    'gateway-whatsapp': 'WhatsApp (QR Code)',
    'whatsapp-business': 'WhatsApp Business API',
    'instagram-messaging': 'Instagram Direct',
    'facebook-messenger': 'Facebook Messenger',
    'telegram-bot': 'Telegram Bot',
    'email-smtp': 'E-mail SMTP',
    'email-resend': 'E-mail nativo',
    'website-widget': 'Widget de Chat',
  };
  return providerId ? names[providerId] || 'a integração' : 'a integração';
}

// Step Indicator Component
interface StepIndicatorProps {
  step: number;
  label: string;
  active: boolean;
  completed: boolean;
}

const StepIndicator: React.FC<StepIndicatorProps> = ({
  step,
  label,
  active,
  completed,
}) => {
  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors"
        style={{
          backgroundColor: active
            ? '#8b5cf6'
            : completed
            ? '#22c55e'
            : '#e5e7eb',
          color: active || completed
            ? 'white'
            : '#64748b',
        }}
      >
        {completed ? '✓' : step}
      </div>
      <span
        className="text-xs"
        style={{
          color: active
            ? '#1e293b'
            : '#64748b',
        }}
      >
        {label}
      </span>
    </div>
  );
};
